import React, { type ComponentType } from 'react';
import { styled } from '@compiled/react';
import FeedSkeleton from '@jira/issue__feed-skeleton/src/view.tsx';
import { token } from '@atlaskit/tokens';
import { IssueViewMetaContainer } from '@atlassian/jira-issue-view-layout-meta-container/src/ui/index.tsx';
import type { ActivitySortOrderType } from '@atlassian/jira-issue-shared-types/src/common/types/activity-sort-order-type.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useUserPreferredWorkItemLayout } from '@atlassian/jira-issue-view-layout/src/services/main.tsx';
import type { mainIssueAggQuery$data } from '@atlassian/jira-relay/src/__generated__/mainIssueAggQuery.graphql.ts';
import { FeedContainer } from '../styled.tsx';
import IssueHistoryServiceDI from '../services/issue-history/index.tsx';
import HistoryItemsView from './history-items/index.tsx';

const META_CONTAINER = 'META_CONTAINER';
const HISTORY_FEED_CONTAINER_TEST_ID = 'issue-history.ui.feed-container';

type Props = {
	issueKey: IssueKey;
	sortOrder: ActivitySortOrderType;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	GroupedActivityEntryComponent?: ComponentType<Record<any, any>>;
	issueViewRelayFragment?: mainIssueAggQuery$data['jira']['issueByKey'] | null;
};

const HistoryItemsDI = HistoryItemsView;

type InternalProps = Props & {
	HistoryItems: typeof HistoryItemsDI;
	IssueHistoryService: typeof IssueHistoryServiceDI;
};

const IssueHistory = ({
	issueKey,
	sortOrder,
	HistoryItems,
	IssueHistoryService,
	GroupedActivityEntryComponent,
	issueViewRelayFragment,
}: InternalProps): React.JSX.Element => {
	const { userPreferredLayout } = useUserPreferredWorkItemLayout();

	return (
		<FeedContainer data-testid={HISTORY_FEED_CONTAINER_TEST_ID}>
			{userPreferredLayout === 'WIDE' && (
				<MetaDataWrapper data-testid="issue-history.ui.meta-data">
					<IssueViewMetaContainer
						key={META_CONTAINER}
						fragmentKey={issueViewRelayFragment ?? null}
					/>
				</MetaDataWrapper>
			)}
			<IssueHistoryService issueKey={issueKey} sortOrder={sortOrder}>
				{/* @ts-expect-error - TS2322 - Type '({ loading, loadingMore, hasMore, loadMore, error, data }: ServiceResult) => Element | null' is not assignable to type '(arg1: ServiceResult) => ReactElement<any, string | JSXElementConstructor<any>>'. */}
				{({ loading, loadingMore, hasMore, loadMore, error, data }) => {
					if (loading) {
						return <FeedSkeleton />;
					}
					if (error) {
						return null;
					}

					return (
						<HistoryItems
							history={data}
							hasMore={hasMore}
							isLoadingMore={loadingMore}
							onLoadMore={loadMore}
							sortOrder={sortOrder}
							GroupedActivityEntryComponent={GroupedActivityEntryComponent}
						/>
					);
				}}
			</IssueHistoryService>
		</FeedContainer>
	);
};

IssueHistory.defaultProps = {
	HistoryItems: HistoryItemsDI,
	IssueHistoryService: IssueHistoryServiceDI,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MetaDataWrapper = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
	flexDirection: 'row',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	alignContent: 'stretch',
	marginLeft: token('space.negative.150'),
	marginBottom: token('space.300'),
	marginTop: token('space.negative.200'),
});

export default IssueHistory;
